import type {
  Adapter,
  MessageSignerWalletAdapter,
  WalletName
} from '@solana/wallet-adapter-base'

export type ConnectedSolanaWallet = {
  adapter: Adapter
  walletAddress: string
}

const STANDARD_CONNECT = 'standard:connect'

type StandardConnectFn = (input?: { silent?: boolean }) => Promise<unknown>

export function findWalletAdapter(
  wallets: { adapter: Adapter }[],
  walletName: string
): Adapter | undefined {
  return wallets.find(wallet => wallet.adapter.name === walletName)?.adapter
}

export function isUserRejectedWalletError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error)

  return message.toLowerCase().includes('user rejected')
}

export function getWalletErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'Wallet was not connected'
}

export function waitForWalletProviderToAttach() {
  return new Promise<void>(resolve => {
    setTimeout(resolve, 0)
  })
}

function isMessageSignerAdapter(
  adapter: Adapter
): adapter is MessageSignerWalletAdapter {
  return 'signMessage' in adapter && typeof adapter.signMessage === 'function'
}

function isAdapterConnected(adapter: Adapter) {
  return Boolean(adapter.connected && adapter.publicKey)
}

function listenOnceForConnection(
  adapter: Adapter,
  resolve: () => void,
  reject: (error: Error) => void
) {
  let settled = false

  const cleanup = () => {
    adapter.off('connect', onConnect)
    adapter.off('error', onError)
  }

  const settle = (action: () => void) => {
    if (settled) return
    settled = true
    cleanup()
    action()
  }

  const onConnect = () => settle(resolve)
  const onError = (error: Error) => settle(() => reject(error))

  adapter.on('connect', onConnect)
  adapter.on('error', onError)

  return { onConnect, onError }
}

export function getStandardConnect(
  adapter: Adapter
): StandardConnectFn | undefined {
  if (!('wallet' in adapter) || !adapter.wallet) return undefined

  const wallet = adapter.wallet as {
    features?: Record<string, { connect?: StandardConnectFn }>
  }

  return wallet.features?.[STANDARD_CONNECT]?.connect
}

export async function waitForAdapterConnected(adapter: Adapter): Promise<void> {
  if (isAdapterConnected(adapter)) return

  await new Promise<void>((resolve, reject) => {
    const { onConnect, onError } = listenOnceForConnection(
      adapter,
      resolve,
      reject
    )

    if (isAdapterConnected(adapter)) {
      onConnect()
      return
    }

    if (adapter.connecting) return

    adapter
      .connect()
      .then(() => {
        if (isAdapterConnected(adapter)) onConnect()
      })
      .catch(onError)
  })
}

export async function authorizeCurrentSolanaAccount(adapter: Adapter) {
  if (adapter.connected) {
    await adapter.disconnect()
  }

  const standardConnect = getStandardConnect(adapter)
  if (standardConnect) {
    await standardConnect({ silent: false })
  }

  await waitForAdapterConnected(adapter)
}

async function selectWallet(
  select: (name: WalletName | null) => void,
  walletName: string
) {
  select(walletName as WalletName)
  await waitForWalletProviderToAttach()
}

async function reconnectIfNeeded(
  adapter: Adapter,
  select: (name: WalletName | null) => void,
  walletName: string
) {
  if (!adapter.connected) return
  await adapter.disconnect()
  await selectWallet(select, walletName)
}

function getConnectedWallet(adapter: Adapter): ConnectedSolanaWallet {
  const walletAddress = adapter.publicKey?.toBase58()
  if (!walletAddress) throw new Error('Wallet was not connected')

  return { adapter, walletAddress }
}

// Always re-authorize the account Phantom currently has selected.
// After logout, autoConnect keeps the previous account connected and
// adapter.connect() becomes a no-op — signMessage then fails for the new one.
export async function connectSelectedSolanaWallet(
  wallets: { adapter: Adapter }[],
  walletName: string,
  select: (name: WalletName | null) => void
): Promise<ConnectedSolanaWallet> {
  const adapter = findWalletAdapter(wallets, walletName)
  if (!adapter) throw new Error('Wallet was not connected')

  await selectWallet(select, walletName)
  await reconnectIfNeeded(adapter, select, walletName)
  await authorizeCurrentSolanaAccount(adapter)

  return getConnectedWallet(adapter)
}

export async function signChallengeWithAdapter(
  adapter: Adapter,
  challenge: string
): Promise<string> {
  if (!isMessageSignerAdapter(adapter)) {
    throw new Error('Wallet does not support signing messages')
  }

  const encoded = new TextEncoder().encode(challenge)
  const signatureBytes = await adapter.signMessage(encoded)

  return Buffer.from(signatureBytes).toString('base64')
}
